import { toast } from "react-toastify";
import { commonHeader, generateEndpoint } from "~/config/apiConfig";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ErrorBoundary } from "~/errors/errorBoundary";
import type { CreateKnowledge, Knowledge } from "~/interface/knowledge";

export class KnowledgeApi {
  private getRequestInfo(method: string, requestBody?: object): RequestInit {
    return {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: true,
      ...{
        body: JSON.stringify(requestBody),
      },
    };
  }

  private getRequestBody(baseBody: object, extraBody?: object): object {
    // ベースとなる共通データと、メソッドごとの追加データをマージ
    return { ...baseBody, ...extraBody };
  }

  async get(id: string, project_id: string, organization_id: string): Promise<Knowledge | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/knowledge/${id}`,
    );

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const knowledge = await response.json();
        return knowledge;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error("❌ Request failed:", {
          status: response.status,
          statusText: response.statusText,
          errorMessage,
        });

        // レスポンスボディも確認
        try {
          const errorBody = await response.text();
          console.error("❌ Error response body:", errorBody);
        } catch (_e) {
          console.error("❌ Could not read error response body");
        }

        return null;
      }
    } catch (error) {
      console.error("🚨 Network or other error:", error);
      return null;
    }
  }

  async getByStatus(
    project_id: string,
    organization_id: string,
    status: "pending" | "approved",
  ): Promise<Knowledge | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/knowledge?status=${status}`,
    );

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const knowledge = await response.json();
        return knowledge;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async create(
    organization_id: string,
    project_id: string,
    knowledge: CreateKnowledge,
  ): Promise<Knowledge | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/knowledge`,
    );

    try {
      const requestInfo = this.getRequestInfo("POST", knowledge);
      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(endpoint, requestBody);

      if (response.ok) {
        const createdKnowledge = await response.json();
        return createdKnowledge;
      } else {
        const errorResponse = await response.json();
        console.error("❌ Create request failed:", errorResponse);
        toast.error(errorResponse.message);
        return null;
      }
    } catch (error) {
      console.error("🚨 Create request error:", error);
      return null;
    }
  }

  async approve(
    organization_id: string,
    project_id: string,
    id: string,
    knowledge: Knowledge,
  ): Promise<boolean | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/knowledge/${id}/approve`,
    );

    try {
      const requestInfo = this.getRequestInfo("POST", knowledge);
      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(endpoint, requestBody);

      if (response.ok) {
        await response.json();
        return true;
      } else {
        console.error("❌ Approve request failed:", {
          status: response.status,
          statusText: response.statusText,
        });

        try {
          const errorResponse = await response.json();
          console.error("❌ Error response:", errorResponse);
          if (errorResponse.message) {
            toast.error(errorResponse.message);
          } else {
            toast.error(TOAST_MESSAGES.KNOWLEDGE.APPROVAL_FAILED);
          }
        } catch (_e) {
          console.error("❌ Could not parse error response");
          toast.error(TOAST_MESSAGES.KNOWLEDGE.APPROVAL_FAILED);
        }
        return null;
      }
    } catch (error) {
      console.error("🚨 Approve request error:", error);
      return null;
    }
  }

  async updateMetaPrompt(
    organization_id: string,
    project_id: string,
    id: string,
    knowledge: Knowledge,
  ): Promise<boolean | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/knowledge/${id}/meta-prompt`,
    );

    try {
      const requestInfo = this.getRequestInfo("POST", knowledge);
      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(endpoint, requestBody);

      if (response.ok) {
        // 成功時はレスポンスボディを確認してからtrueを返す
        await response.json();
        return true;
      } else {
        console.error("❌ UpdateMetaPrompt request failed:", {
          status: response.status,
          statusText: response.statusText,
        });

        try {
          const errorResponse = await response.json();
          console.error("❌ Error response:", errorResponse);
          if (errorResponse.message) {
            toast.error(errorResponse.message);
          } else {
            toast.error(TOAST_MESSAGES.KNOWLEDGE.UPDATE_FAILED);
          }
        } catch (_e) {
          console.error("❌ Could not parse error response");
          toast.error(TOAST_MESSAGES.KNOWLEDGE.UPDATE_FAILED);
        }
        return null;
      }
    } catch (error) {
      console.error("🚨 UpdateMetaPrompt request error:", error);
      return null;
    }
  }
  async updateIsActive(
    organization_id: string,
    project_id: string,
    id: string,
    isActive: boolean,
  ): Promise<boolean | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/knowledge/${id}/active`,
    );

    try {
      const requestInfo = this.getRequestInfo("POST", { isActive });
      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(endpoint, requestBody);

      if (response.ok) {
        // 成功時はレスポンスボディを確認してからtrueを返す
        await response.json();
        return true;
      } else {
        console.error("❌ UpdateIsActive request failed:", {
          status: response.status,
          statusText: response.statusText,
        });

        try {
          const errorResponse = await response.json();
          console.error("❌ Error response:", errorResponse);
          if (errorResponse.message) {
            toast.error(errorResponse.message);
          } else {
            toast.error(TOAST_MESSAGES.KNOWLEDGE.STATUS_UPDATE_FAILED_API);
          }
        } catch (_e) {
          console.error("❌ Could not parse error response");
          toast.error(TOAST_MESSAGES.KNOWLEDGE.STATUS_UPDATE_FAILED_API);
        }
        return null;
      }
    } catch (error) {
      console.error("🚨 UpdateIsActive request error:", error);
      return null;
    }
  }

  async delete(organization_id: string, project_id: string, id: string): Promise<boolean | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/knowledge/${id}`,
    );
    try {
      const requestInfo = this.getRequestInfo("DELETE");
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return true;
      } else {
        const errorResponse = await response.json();
        console.error(errorResponse.message);
        toast.error(errorResponse.message);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
